import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../ui/dialog";
import { Instagram } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQS = [
    { k: "01", t: "Age 18–29" },
    { k: "02", t: "Strong opinions and diverse view points" },
    { k: "03", t: "Comfortable on camera" },
    { k: "04", t: "Passionate about free media, free speech and content creation" },
];

export const Participate = ({ open, onOpenChange }) => {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        age: "",
        city: "",
        topic_interest: "",
        statement: "",
        instagram: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const setField = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const wordCount = (str) =>
        str.trim() ? str.trim().split(/\s+/).length : 0;

    const topicWords = wordCount(form.topic_interest);
    const MAX_WORDS = 500;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (
            !form.full_name ||
            !form.email ||
            !form.age ||
            !form.city ||
            !form.topic_interest ||
            !form.statement
        ) {
            toast.error("Please fill in all required fields.");
            return;
        }
        if (topicWords > MAX_WORDS) {
            toast.error(`Topic answer must be ${MAX_WORDS} words or fewer.`);
            return;
        }
        const age = parseInt(form.age, 10);
        if (Number.isNaN(age) || age < 18 || age > 29) {
            toast.error("You must be 18–29 to apply.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/applications`, {
                ...form,
                age,
            });
            toast.success("Application received. We'll be in touch soon.");
            setForm({
                full_name: "",
                email: "",
                age: "",
                city: "",
                topic_interest: "",
                statement: "",
                instagram: "",
            });
            onOpenChange?.(false);
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Something went wrong. Try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="participate"
            data-testid="participate-section"
            className="relative bg-tip-white text-tip-black py-24 md:py-36 border-t border-black/10"
        >
            <div className="tip-container grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                <div className="lg:col-span-7">
                    <div className="eyebrow text-black/60 mb-6">
                        03 — Participate
                    </div>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.8 }}
                        className="font-display font-black text-5xl sm:text-6xl md:text-7xl lg:text-[96px] tracking-[-0.03em] leading-[0.92]"
                    >
                        Join the
                        <br />
                        <span className="italic">revolution.</span>
                    </motion.h2>
                    <p className="mt-6 max-w-xl text-base md:text-lg text-black/70 leading-relaxed">
                        The India Projekt is actively casting Gen Z Indians for
                        upcoming debates, social experiments and documentary
                        features. Honest voices welcome — across regions,
                        religions and beliefs.
                    </p>

                    <Dialog open={open} onOpenChange={onOpenChange}>
                        <DialogContent
                            data-testid="apply-dialog"
                            data-lenis-prevent
                            className="max-w-lg bg-tip-white text-tip-black border border-black/15 rounded-none p-0 max-h-[85vh] overflow-y-scroll overscroll-contain dialog-scroll"
                        >
                            <div className="p-6 md:p-7">
                                <DialogHeader>
                                    <DialogTitle className="font-display font-black text-2xl md:text-[28px] tracking-tight leading-tight">
                                        Tell us who you are.
                                    </DialogTitle>
                                    <DialogDescription className="text-black/60 text-sm">
                                        We would like to hear your creative
                                        ideas for our next production! Got an
                                        underrated story for us? A topic worth
                                        discussion? A cause worth fighting for?
                                        Let us know and help build an informed
                                        India!
                                    </DialogDescription>
                                </DialogHeader>

                                <form
                                    onSubmit={submit}
                                    className="mt-5 space-y-3"
                                    data-testid="apply-form"
                                >
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="eyebrow text-black/60 text-[10px]">
                                                Full name *
                                            </label>
                                            <Input
                                                data-testid="apply-name"
                                                className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 h-9 text-sm"
                                                value={form.full_name}
                                                onChange={setField("full_name")}
                                                placeholder="Aanya Sharma"
                                            />
                                        </div>
                                        <div>
                                            <label className="eyebrow text-black/60 text-[10px]">
                                                Email *
                                            </label>
                                            <Input
                                                data-testid="apply-email"
                                                type="email"
                                                className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 h-9 text-sm"
                                                value={form.email}
                                                onChange={setField("email")}
                                                placeholder="you@example.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="eyebrow text-black/60 text-[10px]">
                                                Age (18–29) *
                                            </label>
                                            <Input
                                                data-testid="apply-age"
                                                type="number"
                                                min={18}
                                                max={29}
                                                className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 h-9 text-sm"
                                                value={form.age}
                                                onChange={setField("age")}
                                                placeholder="23"
                                            />
                                        </div>
                                        <div>
                                            <label className="eyebrow text-black/60 text-[10px]">
                                                City *
                                            </label>
                                            <Input
                                                data-testid="apply-city"
                                                className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 h-9 text-sm"
                                                value={form.city}
                                                onChange={setField("city")}
                                                placeholder="Bengaluru"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="eyebrow text-black/60 text-[10px]">
                                            Topic you feel strongest about *
                                        </label>
                                        <Textarea
                                            data-testid="apply-topic"
                                            className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 min-h-[90px] text-sm"
                                            value={form.topic_interest}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                const words = wordCount(v);
                                                if (words <= MAX_WORDS) {
                                                    setForm((f) => ({
                                                        ...f,
                                                        topic_interest: v,
                                                    }));
                                                } else {
                                                    const trimmed = v
                                                        .trim()
                                                        .split(/\s+/)
                                                        .slice(0, MAX_WORDS)
                                                        .join(" ");
                                                    setForm((f) => ({
                                                        ...f,
                                                        topic_interest: trimmed,
                                                    }));
                                                }
                                            }}
                                            placeholder="What topic do you feel strongest about? Tell us in your own words..."
                                        />
                                        <div
                                            data-testid="apply-topic-counter"
                                            className={`mt-1 text-[10px] tracking-[0.18em] uppercase ${
                                                topicWords > MAX_WORDS
                                                    ? "text-red-600"
                                                    : "text-black/50"
                                            }`}
                                        >
                                            {topicWords} / {MAX_WORDS} words
                                        </div>
                                    </div>

                                    <div>
                                        <label className="eyebrow text-black/60 text-[10px]">
                                            Your opinion, in 2–4 sentences *
                                        </label>
                                        <Textarea
                                            data-testid="apply-statement"
                                            className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 min-h-[90px] text-sm"
                                            value={form.statement}
                                            onChange={setField("statement")}
                                            placeholder="What do you believe — and why?"
                                        />
                                    </div>

                                    <div>
                                        <label className="eyebrow text-black/60 text-[10px]">
                                            Instagram (optional)
                                        </label>
                                        <Input
                                            data-testid="apply-instagram"
                                            className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-tip-black mt-1.5 h-9 text-sm"
                                            value={form.instagram}
                                            onChange={setField("instagram")}
                                            placeholder="@yourhandle"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="apply-submit"
                                        className="btn-block btn-block-dark w-full mt-3 disabled:opacity-60 py-3 text-[11px]"
                                    >
                                        {submitting ? "Sending..." : "Submit"}
                                    </button>
                                </form>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <p
                        data-testid="participate-instagram-cta"
                        className="mt-8 max-w-xl text-base md:text-lg text-tip-black font-bold leading-relaxed"
                    >
                        Follow us on Instagram to stay updated regarding our
                        casting calls{" "}
                        <a
                            href="https://instagram.com/the.india.projekt"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 align-middle text-tip-black link-underline"
                        >
                            <Instagram size={18} strokeWidth={2} className="inline-block" />
                            @the.india.projekt
                        </a>
                    </p>
                </div>

                <div className="lg:col-span-5">
                    <h3
                        data-testid="who-we-are-looking-for-heading"
                        className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-[-0.02em] leading-[1] mb-8"
                    >
                        Who are we looking for?
                    </h3>
                    <div className="border border-black/15 divide-y divide-black/15">
                        {REQS.map((r) => (
                            <div
                                key={r.k}
                                data-testid={`req-${r.k}`}
                                className="grid grid-cols-[64px_1fr] gap-4 px-6 py-7 hover:bg-tip-paper transition-colors"
                            >
                                <span className="font-display text-2xl font-black text-tip-saffron">
                                    {r.k}
                                </span>
                                <div className="flex items-center">
                                    <div className="font-display font-bold text-xl tracking-tight leading-snug">
                                        {r.t}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
